import { useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin as isAdminRole } from "@/utils/roles";
import { STAGE_META } from "@/utils/quoteStages";
import { QuoteStageTracker } from "@/components/quotes/QuoteStageTracker";
import { QuotePayments } from "@/components/quotes/QuotePayments";
import { QuoteHistory } from "@/components/quotes/QuoteHistory";

export const QuoteLifecycle = ({ quote, onUpdated }) => {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.role);
  const [busy, setBusy] = useState(false);

  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onUpdated(data); if (msg) toast.success(msg); return true; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Update failed"); return false; }
    finally { setBusy(false); }
  };

  const changeStage = (status) => {
    if (status === "paid" && quote.balance > 0 && !window.confirm(`Mark as Paid? This records the remaining balance of £${Number(quote.balance).toFixed(2)} as received today.`)) return;
    if (status === "refunded" && !window.confirm("Mark this quote as Refunded? The linked payment record will be marked refunded too.")) return;
    run(() => api.put(`/quotes/${quote.id}/stage`, { status }), `Quote marked ${STAGE_META[status].label}`);
  };

  return (
    <div className="mt-2 space-y-2" data-testid={`quote-lifecycle-${quote.id}`}>
      <QuoteStageTracker quote={quote} isAdmin={isAdmin} busy={busy} onChange={changeStage} />
      <QuotePayments
        quote={quote} isAdmin={isAdmin} busy={busy}
        onAdd={(p) => run(() => api.post(`/quotes/${quote.id}/payments`, p), "Payment recorded")}
        onRemove={(id) => window.confirm("Remove this payment?") && run(() => api.delete(`/quotes/${quote.id}/payments/${id}`), "Payment removed")}
      />
      {(quote.invoice_reference || quote.po_reference) && (
        <p className="text-[10px] text-muted-foreground">
          {quote.invoice_reference && <>Invoice ref: <span className="font-mono text-slate-700">{quote.invoice_reference}</span> </>}
          {quote.po_reference && <>PO: <span className="font-mono text-slate-700">{quote.po_reference}</span></>}
        </p>
      )}
      <QuoteHistory quote={quote} />
    </div>
  );
};
